import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AnchorIcon } from "@/components/icons/AnchorIcon";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowRight, Sparkles, KeyRound } from "lucide-react";
import { EmailConfirmationBanner } from "@/components/auth/EmailConfirmationBanner";
import analytics from "@/lib/segment";
import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";

type FlowStep = "survey" | "loading" | "createAccount";

const carCountOptions = [
  { value: "0", label: "0 cars (Just Planning)" },
  { value: "1-2", label: "1 - 2 cars (Side Hustle)" },
  { value: "3-5", label: "3 - 5 cars (Scaling)" },
  { value: "6+", label: "6+ cars (Full Business)" },
];

const zoomCallOptions = [
  { value: "yes", label: "Yes, I'd love to help." },
  { value: "no", label: "No, just keep me on the list." },
];

export default function OnboardingSurvey() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [carCount, setCarCount] = useState("");
  const [biggestHeadache, setBiggestHeadache] = useState("");
  const [openToCall, setOpenToCall] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ carCount?: string; biggestHeadache?: string; openToCall?: string }>({});
  
  // Flow states
  const [flowStep, setFlowStep] = useState<FlowStep>("survey");
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string>();
  const captchaRef = useRef<TurnstileInstance>(null);
  const [signupComplete, setSignupComplete] = useState(false);
  useEffect(() => {
    // Get email from URL params or session storage
    const emailParam = searchParams.get("email");
    const storedEmail = sessionStorage.getItem("waitlist_email");
    
    if (emailParam) {
      setEmail(emailParam);
      sessionStorage.setItem("waitlist_email", emailParam);
    } else if (storedEmail) {
      setEmail(storedEmail);
    } else {
      // No email found, redirect to landing
      navigate("/");
    }
  }, [searchParams, navigate]);

  const validateForm = () => {
    const newErrors: typeof errors = {};
    
    if (!carCount) {
      newErrors.carCount = "Please select how many cars you have.";
    }
    if (!biggestHeadache.trim()) {
      newErrors.biggestHeadache = "Please describe your biggest headache.";
    }
    if (!openToCall) {
      newErrors.openToCall = "Please let us know if you're open to a call.";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSurveySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Save to database
      const { error: dbError } = await supabase
        .from("survey_responses")
        .insert({
          email,
          car_count: carCount,
          biggest_headache: biggestHeadache.trim(),
          open_to_call: openToCall === "yes",
        });

      if (dbError) {
        console.error("Database error:", dbError);
        throw new Error("Failed to save survey response");
      }

      // Submit to ActiveCampaign via edge function
      const { error: acError } = await supabase.functions.invoke("submit-survey-activecampaign", {
        body: {
          email,
          carCount,
          openToCall: openToCall === "yes",
        },
      });

      if (acError) {
        console.error("ActiveCampaign error:", acError);
        // Don't throw - we still want to continue even if AC fails
      }
      
      // Track survey submission
      analytics.track("Onboarding survey submitted", {
        car_count: carCount,
        biggest_headache: biggestHeadache.trim(),
        open_to_call: openToCall === "yes",
      });
      
      // Move to loading step
      proceedToLoading();
    } catch (error) {
      console.error("Survey submission error:", error);
      toast({
        title: "Error",
        description: "There was a problem submitting your survey. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipSurvey = () => {
    analytics.track("Onboarding survey skipped");
    proceedToLoading();
  };

  const proceedToLoading = () => {
    setFlowStep("loading");
    
    // Show loading animation then account creation screen
    setTimeout(() => {
      setFlowStep("createAccount");
    }, 2000);
  };

  const resetCaptcha = () => {
    captchaRef.current?.reset();
    setCaptchaToken(undefined);
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreatingAccount(true);
    setAccountError("");
    
    const signupCompanyName = companyName || `${fullName}'s Company`;
    
    try {
      // Company is created automatically by database trigger using company_name from metadata
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/app/launch-kit`,
          captchaToken,
          data: {
            full_name: fullName,
            subscription_tier: "free",
            company_name: signupCompanyName,
          },
        },
      });

      if (signUpError) {
        console.error("Signup error:", signUpError);
        resetCaptcha();
        setAccountError(signUpError.message);
        return;
      }

      if (!authData.user) {
        resetCaptcha();
        setAccountError("Failed to create account. Please try again.");
        return;
      }

      // Check if user already exists (Supabase returns user with empty identities array)
      if (authData.user.identities && authData.user.identities.length === 0) {
        resetCaptcha();
        toast({
          title: "Account already exists",
          description: "Please sign in with your existing account.",
        });
        navigate("/auth?email=" + encodeURIComponent(email));
        return;
      }

      // Track signup
      analytics.track("Signed Up", {
        plan_type: "free",
        source: "onboarding_flow",
        company_name: signupCompanyName,
      });

      // Clear session storage
      sessionStorage.removeItem("waitlist_email");

      // Show confirmation banner instead of navigating
      resetCaptcha();
      setSignupComplete(true);
      toast({ 
        title: "Account created!", 
        description: "Please check your email to confirm your account." 
      });
    } catch (error: any) {
      console.error("Account creation error:", error);
      resetCaptcha();
      setAccountError(error.message || "Something went wrong. Please try again.");
    } finally {
      setIsCreatingAccount(false);
    }
  };

  // Loading screen
  if (flowStep === "loading") {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="text-center">
          <div className="mb-8">
            <div className="relative inline-flex">
              <AnchorIcon className="h-16 w-16 text-white animate-pulse" />
              <Sparkles className="absolute -top-2 -right-2 h-6 w-6 text-yellow-400 animate-bounce" />
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Personalizing your dashboard...
          </h2>
          <p className="text-white/70 mb-8">
            Setting up your HostCaptain experience
          </p>
          <div className="flex justify-center gap-2">
            <div className="w-3 h-3 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <div className="w-3 h-3 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <div className="w-3 h-3 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        </div>
      </div>
    );
  }

  // Account creation screen
  if (flowStep === "createAccount") {
    // Show confirmation banner after successful signup
    if (signupComplete) {
      return (
        <div className="min-h-screen bg-gradient-hero">
          <div className="container mx-auto px-6 py-16">
            <div className="max-w-md mx-auto">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-2 mb-6">
                  <AnchorIcon className="h-8 w-8 text-white" />
                  <span className="text-2xl font-bold text-white">HostCaptain</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Verify your email
                </h1>
                <p className="text-lg text-white/80">
                  One last step before you can access your dashboard
                </p>
              </div>

              <Card className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardContent className="p-8 space-y-4">
                  <EmailConfirmationBanner email={email} variant="dark" />
                  <p className="text-sm text-white/60 text-center">
                    Didn't receive the email? Check your spam folder or{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/auth?email=" + encodeURIComponent(email))}
                      className="text-white/80 hover:text-white underline"
                    >
                      try signing in
                    </button>
                    .
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-hero">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-md mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-6">
                <AnchorIcon className="h-8 w-8 text-white" />
                <span className="text-2xl font-bold text-white">HostCaptain</span>
              </div>
              
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 text-green-300 text-sm mb-6">
                <Sparkles className="h-4 w-4" />
                <span>Dashboard ready!</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Create Your Account
              </h1>
              <p className="text-lg text-white/80">
                Set up your password to access your personalized dashboard.
              </p>
            </div>

            {/* Account Creation Card */}
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardContent className="p-8">
                <form onSubmit={handleCreateAccount} className="space-y-4">
                  {/* Email display */}
                  <div className="text-center mb-4">
                    <p className="text-white/60 text-sm mb-1">Creating account for:</p>
                    <p className="text-white font-medium">{email}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-white">Full Name</Label>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="John Smith"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyName" className="text-white">Company Name</Label>
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="My Rental Business"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-white">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                    <p className="text-xs text-white/50">Must be at least 6 characters</p>
                  </div>
                  
                  {accountError && (
                    <p className="text-sm text-destructive text-center">{accountError}</p>
                  )}

                  {/* Turnstile CAPTCHA */}
                  <div className="flex justify-center">
                    <Turnstile
                      ref={captchaRef}
                      siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(undefined)}
                    />
                  </div>

                  {/* Create Account Button */}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-primary hover:opacity-90 py-6 text-lg"
                    disabled={isCreatingAccount || !captchaToken}
                  >
                    {isCreatingAccount ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <KeyRound className="mr-2 h-5 w-5" />
                        Create Account
                      </>
                    )}
                  </Button>
                  
                  <p className="text-center text-white/50 text-sm">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/auth?email=" + encodeURIComponent(email))}
                      className="text-white/80 hover:text-white underline"
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Survey screen (default)
  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-6">
              <AnchorIcon className="h-8 w-8 text-white" />
              <span className="text-2xl font-bold text-white">HostCaptain</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              You're on the list! 🎉
            </h1>
            <p className="text-xl text-white/80 mb-2">
              To customize your experience, tell us about your fleet.
            </p>
            <p className="text-lg text-white/70">
              There are only three short questions!
            </p>
          </div>

          {/* Survey Form */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardContent className="p-8">
              <form onSubmit={handleSurveySubmit} className="space-y-8">
                {/* Question 1: Car Count */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold text-white">
                    How many cars do you currently have on Turo?
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <RadioGroup
                    value={carCount}
                    onValueChange={(value) => {
                      setCarCount(value);
                      if (errors.carCount) {
                        setErrors((prev) => ({ ...prev, carCount: undefined }));
                      }
                    }}
                    className="space-y-3"
                  >
                    {carCountOptions.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <RadioGroupItem
                          value={option.value}
                          id={`car-${option.value}`}
                          className="border-white/50 text-white"
                        />
                        <Label
                          htmlFor={`car-${option.value}`}
                          className="text-white cursor-pointer flex-1"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {errors.carCount && (
                    <p className="text-sm text-destructive">{errors.carCount}</p>
                  )}
                </div>

                {/* Question 2: Biggest Headache */}
                <div className="space-y-4">
                  <Label htmlFor="headache" className="text-lg font-semibold text-white">
                    What is the single biggest headache you face in your daily operations?
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Textarea
                    id="headache"
                    value={biggestHeadache}
                    onChange={(e) => {
                      setBiggestHeadache(e.target.value);
                      if (errors.biggestHeadache) {
                        setErrors((prev) => ({ ...prev, biggestHeadache: undefined }));
                      }
                    }}
                    placeholder="Tell us about your biggest challenge..."
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 min-h-[120px]"
                  />
                  {errors.biggestHeadache && (
                    <p className="text-sm text-destructive">{errors.biggestHeadache}</p>
                  )}
                </div>

                {/* Question 3: Zoom Call */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold text-white">
                    Would you be open to a 15-minute Zoom call to share your feedback and help shape HostCaptain's features?
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <RadioGroup
                    value={openToCall}
                    onValueChange={(value) => {
                      setOpenToCall(value);
                      if (errors.openToCall) {
                        setErrors((prev) => ({ ...prev, openToCall: undefined }));
                      }
                    }}
                    className="space-y-3"
                  >
                    {zoomCallOptions.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <RadioGroupItem
                          value={option.value}
                          id={`call-${option.value}`}
                          className="border-white/50 text-white"
                        />
                        <Label
                          htmlFor={`call-${option.value}`}
                          className="text-white cursor-pointer flex-1"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {errors.openToCall && (
                    <p className="text-sm text-destructive">{errors.openToCall}</p>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-primary hover:opacity-90 py-6 text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Continue to Setup
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
              
              {/* Skip Link */}
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={handleSkipSurvey}
                  className="text-white/60 hover:text-white/80 text-sm underline underline-offset-4 transition-colors"
                >
                  Skip
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
